import { useMemo } from "react";

import BaseDataTable, {
    IDataRow,
} from "../../../components/ui/baseDataTable";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import BaseDataTableFrame, {
    BaseDataTableEmptyState,
    BaseDataTableSaveFooter,
} from "../../../components/ui/BaseDataTableFrame";
import {
    SanctionEventRow,
    useSanctionEventsTableVM,
} from "../Logic/useSanctionEventsTableVM";
import { makeSanctionEventColumns } from "./sanctionEventColumns";

export const SanctionEventsTable: React.FC = () => {
    const vm = useSanctionEventsTableVM();

    const dataRows: IDataRow<SanctionEventRow>[] = useMemo(
      () =>
        vm.rows.map((row) => ({
          data: row,
          isEditable: !vm.isSaving,
          widgets: vm.isSaving
            ? []
            : row.isAdded
            ? ["add", "delete"]
            : ["add"],
          onAdd: vm.addRow,
          onDelete: vm.deleteRow,
        })),
      [vm.rows, vm.isSaving, vm.addRow, vm.deleteRow],
    );

    const columnConfigs = makeSanctionEventColumns(vm); //[vm] doesn't meaningfully memoize, hence no `useMemo`

    if (vm.isLoading) {
      return (
        <CustomLoadingSpinner
          title="Loading sanction events..."
          subtitle="Fetching sanction event records for this case."
          minHeight="min-h-[160px]"
        />
      );
    }

    if (vm.error) {
      return (
        <ErrorDisplay
          error="Unable to load sanction events. Please try again."
          onRetry={vm.retry}
        />
      );
    }

    return (
      <div className="w-full flex flex-col">
        <BaseDataTableFrame
          title="Sanction Events"
          subtitle="Track sanction authority, sanction type, date, and facility-wise sanctioned amounts"
        >
          <BaseDataTable<SanctionEventRow>
            columnConfigs={columnConfigs}
            dataRows={dataRows}
            showDefaultTableBody={dataRows.length === 0}
            defaultTableBody={
              <BaseDataTableEmptyState
                title="No sanction event records"
                subtitle="Add sanction event records for this case"
                onAdd={vm.addRow}
              />
            }
          />
        </BaseDataTableFrame>

        {vm.hasChanges && (
          <BaseDataTableSaveFooter
            isSaving={vm.isSaving}
            disabled={vm.hasValidationErrors}
            onSave={vm.save}
            onDiscard={vm.discard}
          />
        )}
      </div>
    );
};
